/**
 * @file Computes the extra top offset the layout needs for its
 * warning alerts.
 */
import { getConfig } from "@components/Config";
import { ALERT_HEIGHT } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";

import hasInvalidBankAccounts from "./hasInvalidBankAccounts";
import type { BankAccount, Project } from "./hasInvalidBankAccounts";

/**
 * Returns the layout's top offset in pixels, based on how many
 * warning alerts (two-factor authentication, invalid bank accounts)
 * are showing.
 */
const useLayoutTopOffset = (
  bankAccounts?: BankAccount[],
  projects?: Project[],
): number => {
  const { identity } = useAppContext() as AppContext;
  const is2FaRequired = getConfig<boolean>("is2FaRequired");

  const { is2FaEnabled } = identity;

  const hasTwoFactorAuthenticationAlert = is2FaRequired && !is2FaEnabled;
  const hasInvalidBankAccountsAlert = hasInvalidBankAccounts(bankAccounts, projects);

  const layoutAlerts = [
    hasTwoFactorAuthenticationAlert,
    hasInvalidBankAccountsAlert,
  ];

  const enabledLayoutAlerts = layoutAlerts.filter((value) => value);
  const layoutAlertsCount = enabledLayoutAlerts.length;
  const layoutTopOffset = layoutAlertsCount * ALERT_HEIGHT;

  return layoutTopOffset;
};

export default useLayoutTopOffset;
